import {
    createCustomField as createCustomFieldApi,
    deleteCustomField as deleteCustomFieldApi,
    getAllCustomFields,
    updateCustomField as updateCustomFieldApi,
} from 'core/service/custom-fields/custom-fields.service';
import { CustomField, CustomFieldType } from 'core/service/custom-fields/custom-field.type';
import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { usePocQuery } from 'core/custom-hooks/use-poc-query.hook';
import { updateQuery } from 'core/query-client/query-client';
import { QUERY_KEYS } from 'core/query-client/query-keys.constants';
import { executeRequest } from 'core/utils/backend-service';
import { useCallback, useMemo } from 'react';

interface UseCustomFieldsHookResponse {
    customFields: CustomField[];
    operatorsCustomFields: CustomField[];
    devicesCustomFields: CustomField[];
    refreshCustomFields: () => Promise<void>;
    createCustomField: (customField: CustomField) => Promise<void>;
    updateCustomField: (customField: CustomField) => Promise<void>;
    deleteCustomField: (customFieldId: string) => Promise<void>;
    getCustomFieldByType: (type: CustomFieldType) => CustomField[];
    isLoading: boolean;
}

const fetchCustomFields = async (): Promise<CustomField[]> => {
    const response = await getAllCustomFields();
    return response.result.customFields;
};

export const useCustomFields = (): UseCustomFieldsHookResponse => {
    const { data: customFields = [], isLoading: isQueryLoading } = usePocQuery({
        queryKey: QUERY_KEYS.CUSTOM_FIELDS,
        queryFn: () => executeRequest(fetchCustomFields),
    });

    const operatorsCustomFields = useMemo(
        () => customFields.filter((field) => field.type === 'OPERATORS'),
        [customFields]
    );
    const devicesCustomFields = useMemo(() => customFields.filter((field) => field.type === 'DEVICES'), [customFields]);

    const refreshCustomFields = useCallback(async (): Promise<void> => {
        await updateQuery(QUERY_KEYS.CUSTOM_FIELDS);
    }, []);

    const onMutationSuccess = useCallback(() => updateQuery(QUERY_KEYS.CUSTOM_FIELDS), []);

    const createMutation = usePocMutation({
        mutationFn: (customField: CustomField) => createCustomFieldApi(customField),
        onSuccess: onMutationSuccess,
    });
    const updateMutation = usePocMutation({
        mutationFn: (customField: CustomField) => updateCustomFieldApi(customField),
        onSuccess: onMutationSuccess,
    });
    const deleteMutation = usePocMutation({
        mutationFn: (customFieldId: string) => deleteCustomFieldApi(customFieldId),
        onSuccess: onMutationSuccess,
    });

    const getCustomFieldByType = useCallback(
        (type: CustomFieldType): CustomField[] => {
            const customsFieldsByType: Record<CustomFieldType, CustomField[]> = {
                DEVICES: devicesCustomFields,
                OPERATORS: operatorsCustomFields,
            };
            return customsFieldsByType[type] || [];
        },
        [devicesCustomFields, operatorsCustomFields]
    );

    const isLoading =
        isQueryLoading || createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

    const createCustomField = useCallback(
        async (customField: CustomField): Promise<void> => {
            await createMutation.mutateAsync(customField);
        },
        [createMutation]
    );
    const updateCustomField = useCallback(
        async (customField: CustomField): Promise<void> => {
            await updateMutation.mutateAsync(customField);
        },
        [updateMutation]
    );
    const deleteCustomField = useCallback(
        async (customFieldId: string): Promise<void> => {
            await deleteMutation.mutateAsync(customFieldId);
        },
        [deleteMutation]
    );

    return {
        isLoading,
        customFields,
        operatorsCustomFields,
        devicesCustomFields,
        refreshCustomFields,
        createCustomField,
        deleteCustomField,
        updateCustomField,
        getCustomFieldByType,
    };
};
